/**
 * Aba "Notificações do operador" da Tela A2 (Config Tenant) — Frente N N3a/N3b.
 *
 * Expõe o campo REAL `operatorNotificationPolicy` do `tenant-config` (R15): a
 * conformidade trabalhista por construção (direito à desconexão + sobreaviso).
 * Fora das janelas de expediente, o push do TRABALHO é suprimido no servidor
 * (a informação continua íntegra ao abrir o app; quem está de plantão mantém o
 * tier `urgent` furando). O gate (`OperatorPushGuardService`) já existe — esta aba
 * dá ao admin a config por-tenant. Escrita gated em `tenant-config-manage`.
 *
 * Resolução efetiva única BE+FE: `resolveOperatorNotificationPolicy` de
 * `@lume/shared/push` — a UI mostra exatamente o que o runtime fará. Master switch
 * opt-in (P12): desligado ⇒ PATCH `null` (nunca suprime, byte-idêntico N1/B7).
 *
 * Admin Hub T9 — os botões locais Descartar/Salvar saíram; a seção registra na
 * `StickySaveBar` do domínio via `useSectionSave` (mesmo movimento do `GeneralTab`,
 * T7, e do `SilentTab`/`BehavioralTab`/`ConductTab`, T8). ⚠️ Grava agora com
 * `update.mutateAsync` (não `update.mutate`) — o `save()` da registration devolve
 * `Promise<void>`, e o `StickySaveBar` precisa aguardar o PATCH terminar antes de
 * decidir sucesso/erro do toast (mesma correção que a T7 aplicou em `GeneralTab`).
 * O PATCH real sempre carrega o objeto `operatorNotificationPolicy` INTEIRO (não
 * há uma chave por campo) — `changedLabels` só nomeia, para o toast, quais dos 4
 * campos do form mudaram; o payload de `save()`/`undo()` é sempre o objeto
 * completo (ou `null`), pela MESMA mutation nos dois sentidos. Cabeçalho próprio
 * ("Notificações do operador" + explicação) também saiu — dentro de uma
 * `DomainSection` o título/descrição já vêm do catálogo (item.label/item.efeito),
 * mesmo racional que `GeneralTab`/`SilentTab` já registraram. O aviso de "sem
 * janelas" (antes no footer) continua vivo — é honestidade sobre o estado ATUAL
 * do form, não uma mensagem de resultado de save, então não migrou pro toast.
 */

import { resolveOperatorNotificationPolicy } from '@lume/shared/push';
import { useEffect, useMemo, useRef, useState } from 'react';

import { COMMON_TIMEZONES } from '../../scheduling-resources/resource-labels';
import { useSectionSave } from '../hub/save-bar-context';

import { OPERATOR_SHIFT_TAB_FIELD_LABELS } from './config-tenant-labels';
import { useTenantConfig, useUpdateTenantConfig } from './use-tenant-config';

import type {
  OperatorNotificationPolicyRequest,
  OperatorShiftWindowRequest,
  SerializedTenantConfig,
  UpdateTenantConfigRequest,
} from './types';
import type { JSX } from 'react';

/** Rótulos curtos dos dias (0=Domingo … 6=Sábado), na ordem da semana. */
const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

function minuteToHHMM(m: number): string {
  // 1440 (fim do dia) exibe como 24:00 (não existe no <input type="time">, só no resumo).
  if (m === 1440) return '24:00';
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
}

function hhmmToStartMinute(v: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(v);
  if (match === null) return null;
  const h = Number.parseInt(match[1] ?? '', 10);
  const min = Number.parseInt(match[2] ?? '', 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min; // 0-1439
}

/** Fim: "00:00" no seletor significa fim do dia (1440), não início — evita janela zero. */
function hhmmToEndMinute(v: string): number | null {
  const m = hhmmToStartMinute(v);
  if (m === null) return null;
  return m === 0 ? 1440 : m; // 1-1440
}

interface FormState {
  enabled: boolean;
  timeZone: string;
  windows: OperatorShiftWindowRequest[];
  operatorCanSelfMarkOnCall: boolean;
}

function toForm(c: SerializedTenantConfig): FormState {
  const p = resolveOperatorNotificationPolicy(c.operatorNotificationPolicy);
  return {
    enabled: p.enabled,
    timeZone: p.timeZone,
    windows: p.shiftWindows.map((w) => ({
      daysOfWeek: [...w.daysOfWeek],
      startMinute: w.startMinute,
      endMinute: w.endMinute,
    })),
    operatorCanSelfMarkOnCall: p.operatorCanSelfMarkOnCall,
  };
}

/** Form → PATCH. Desligado ⇒ `null` (limpa — opt-in P12); ligado ⇒ policy explícita. */
function buildPolicy(form: FormState): OperatorNotificationPolicyRequest | null {
  if (!form.enabled) return null;
  return {
    version: 1,
    enabled: true,
    timeZone: form.timeZone,
    shiftWindows: form.windows,
    operatorCanSelfMarkOnCall: form.operatorCanSelfMarkOnCall,
  };
}

function windowSummary(w: OperatorShiftWindowRequest): string {
  const days = [...w.daysOfWeek]
    .sort((a, b) => a - b)
    .map((d) => DAY_LABELS[d])
    .join(', ');
  const crosses = w.endMinute <= w.startMinute;
  return `${days} · ${minuteToHHMM(w.startMinute)}–${minuteToHHMM(w.endMinute)}${
    crosses ? ' (vira o dia)' : ''
  }`;
}

export function OperatorShiftTab({ canManage }: { readonly canManage: boolean }): JSX.Element {
  const query = useTenantConfig();
  const update = useUpdateTenantConfig();
  const [form, setForm] = useState<FormState | null>(null);

  // Estado do formulário de "adicionar janela".
  const [draftDays, setDraftDays] = useState<Set<number>>(new Set([1, 2, 3, 4, 5]));
  const [draftStart, setDraftStart] = useState('08:00');
  const [draftEnd, setDraftEnd] = useState('18:00');
  const [draftError, setDraftError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data !== undefined) setForm(toForm(query.data));
  }, [query.data]);

  const serverForm = useMemo(
    () => (query.data !== undefined ? toForm(query.data) : null),
    [query.data],
  );

  const isDirty = useMemo(() => {
    if (form === null || serverForm === null) return false;
    return JSON.stringify(form) !== JSON.stringify(serverForm);
  }, [form, serverForm]);

  // Só os 4 campos reais do form que MUDARAM — o PATCH em si sempre carrega o
  // objeto `operatorNotificationPolicy` INTEIRO (não há 1 chave por campo);
  // isto é só para o toast NOMEAR o que o usuário alterou.
  const changedLabels = useMemo(() => {
    if (form === null || serverForm === null) return [];
    const labels: string[] = [];
    if (form.enabled !== serverForm.enabled) {
      labels.push(OPERATOR_SHIFT_TAB_FIELD_LABELS.enabled);
    }
    if (form.timeZone !== serverForm.timeZone) {
      labels.push(OPERATOR_SHIFT_TAB_FIELD_LABELS.timeZone);
    }
    if (JSON.stringify(form.windows) !== JSON.stringify(serverForm.windows)) {
      labels.push(OPERATOR_SHIFT_TAB_FIELD_LABELS.windows);
    }
    if (form.operatorCanSelfMarkOnCall !== serverForm.operatorCanSelfMarkOnCall) {
      labels.push(OPERATOR_SHIFT_TAB_FIELD_LABELS.operatorCanSelfMarkOnCall);
    }
    return labels;
  }, [form, serverForm]);

  const previousValues = useRef<UpdateTenantConfigRequest | null>(null);

  const handleDiscard = (): void => {
    if (serverForm !== null) setForm(serverForm);
    update.reset();
  };

  // Precisa ser chamado incondicionalmente em todo render (regra dos hooks) —
  // antes dos returns condicionais de loading/erro abaixo.
  useSectionSave(
    isDirty && canManage && form !== null && serverForm !== null
      ? {
          sectionId: 'notificacoes-do-operador',
          changedLabels,
          save: async () => {
            // Captura os valores ANTIGOS (objeto inteiro, ou `null`) — mesma
            // mutation reaplica isto no `undo`.
            previousValues.current = { operatorNotificationPolicy: buildPolicy(serverForm) };
            await update.mutateAsync({ operatorNotificationPolicy: buildPolicy(form) });
          },
          discard: handleDiscard,
          undo: async () => {
            if (previousValues.current !== null) {
              await update.mutateAsync(previousValues.current);
            }
          },
        }
      : null,
  );

  if (query.isLoading) {
    return <p className="text-sm text-text-secondary">Carregando configurações…</p>;
  }
  if (query.isError || query.data === undefined || form === null) {
    return <p className="text-sm text-red-600">Falha ao carregar as configurações do tenant.</p>;
  }

  const fieldsDisabled = !canManage || update.isPending;
  const noWindows = form.enabled && form.windows.length === 0;

  const addWindow = (): void => {
    const days = [...draftDays].sort((a, b) => a - b);
    if (days.length === 0) {
      setDraftError('Escolha ao menos um dia.');
      return;
    }
    const startMinute = hhmmToStartMinute(draftStart);
    const endMinute = hhmmToEndMinute(draftEnd);
    if (startMinute === null || endMinute === null) {
      setDraftError('Horário inválido.');
      return;
    }
    if (startMinute === endMinute) {
      setDraftError('A janela não pode começar e terminar no mesmo horário.');
      return;
    }
    // Dedupe de janela inteira (dias+horas idênticos) — evita persistir uma janela
    // redundante e colisão de key na lista.
    const duplicate = (form?.windows ?? []).some(
      (w) =>
        w.startMinute === startMinute &&
        w.endMinute === endMinute &&
        w.daysOfWeek.length === days.length &&
        w.daysOfWeek.every((d, i) => d === days[i]),
    );
    if (duplicate) {
      setDraftError('Essa janela já foi adicionada.');
      return;
    }
    setDraftError(null);
    setForm((f) =>
      f === null
        ? f
        : { ...f, windows: [...f.windows, { daysOfWeek: days, startMinute, endMinute }] },
    );
  };

  const removeWindow = (index: number): void => {
    setForm((f) => (f === null ? f : { ...f, windows: f.windows.filter((_, i) => i !== index) }));
  };

  const toggleDraftDay = (day: number): void => {
    setDraftDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  return (
    <div className="max-w-2xl">
      {/* Header próprio saiu (Admin Hub T9) — DomainSection já supre título
          (item.label) + descrição (item.efeito) quando esta aba é composta no
          hub, mesmo racional de GeneralTab (T7) e SilentTab/BehavioralTab/
          ConductTab (T8). */}
      <div className="space-y-6">
        {/* Master switch (opt-in P12) */}
        <div className="flex items-center justify-between rounded-xl border border-border-subtle bg-surface p-4">
          <div className="pr-4">
            <h3 className="text-sm font-medium text-text-primary">
              Silenciar push fora do expediente
            </h3>
            <p className="mt-0.5 text-xs text-text-secondary">
              Desligado, o push do operador segue a regra padrão (sem restrição de horário).
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.enabled}
            aria-label="Silenciar push fora do expediente"
            disabled={fieldsDisabled}
            onClick={() => {
              setForm((f) => (f === null ? f : { ...f, enabled: !f.enabled }));
            }}
            className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
              form.enabled ? 'bg-accent' : 'bg-border-subtle'
            }`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                form.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Fuso horário */}
        <div
          className={`rounded-xl border border-border-subtle bg-surface p-4 ${form.enabled ? '' : 'opacity-50'}`}
        >
          <h3 className="text-sm font-medium text-text-primary">Fuso horário do expediente</h3>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            As janelas de expediente abaixo são interpretadas neste fuso.
          </p>
          <select
            aria-label="Fuso horário do expediente"
            className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm"
            value={form.timeZone}
            disabled={fieldsDisabled || !form.enabled}
            onChange={(e) => {
              setForm((f) => (f === null ? f : { ...f, timeZone: e.target.value }));
            }}
          >
            {COMMON_TIMEZONES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
            {!COMMON_TIMEZONES.some((t) => t.value === form.timeZone) && (
              <option value={form.timeZone}>{form.timeZone}</option>
            )}
          </select>
        </div>

        {/* Janelas de expediente */}
        <div
          className={`rounded-xl border border-border-subtle bg-surface p-4 ${form.enabled ? '' : 'opacity-50'}`}
        >
          <h3 className="text-sm font-medium text-text-primary">Janelas de expediente</h3>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            Dentro destas janelas, o push flui normalmente. Fora delas, é silenciado. Uma janela que
            termina antes de começar (ex.: 22:00–06:00) vira o dia (madrugada).
          </p>

          {form.windows.length > 0 ? (
            <ul className="mb-3 space-y-2">
              {form.windows.map((w, i) => (
                <li
                  key={`${w.daysOfWeek.join(',')}-${w.startMinute}-${w.endMinute}`}
                  className="flex items-center justify-between rounded-lg border border-border-subtle px-3 py-2 text-sm"
                >
                  <span className="text-text-primary">{windowSummary(w)}</span>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => removeWindow(i)}
                      disabled={update.isPending}
                      aria-label={`Remover janela ${windowSummary(w)}`}
                      className="text-xs text-red-600 hover:underline disabled:opacity-50"
                    >
                      Remover
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-3 text-xs text-text-secondary">
              {form.enabled
                ? 'Nenhuma janela — com o silenciamento ligado, o push de trabalho fica sempre em silêncio (só plantão urgente fura).'
                : 'Nenhuma janela configurada.'}
            </p>
          )}

          {canManage && (
            <div className="rounded-lg border border-dashed border-border-subtle p-3">
              <p className="mb-2 text-xs font-medium text-text-secondary">Adicionar janela</p>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {DAY_LABELS.map((label, day) => {
                  const selected = draftDays.has(day);
                  return (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={selected}
                      disabled={fieldsDisabled || !form.enabled}
                      onClick={() => toggleDraftDay(day)}
                      className={`rounded-full px-2.5 py-1 text-xs transition-colors disabled:opacity-50 ${
                        selected
                          ? 'bg-accent text-white'
                          : 'border border-border-subtle text-text-secondary hover:bg-app-bg'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs text-text-secondary">
                  <span className="mb-1 block">Início</span>
                  <input
                    type="time"
                    aria-label="Início da janela"
                    className="rounded border border-border-subtle bg-surface px-2 py-1 text-sm"
                    value={draftStart}
                    disabled={fieldsDisabled || !form.enabled}
                    onChange={(e) => setDraftStart(e.target.value)}
                  />
                </label>
                <label className="text-xs text-text-secondary">
                  <span className="mb-1 block">Fim</span>
                  <input
                    type="time"
                    aria-label="Fim da janela"
                    className="rounded border border-border-subtle bg-surface px-2 py-1 text-sm"
                    value={draftEnd}
                    disabled={fieldsDisabled || !form.enabled}
                    onChange={(e) => setDraftEnd(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  onClick={addWindow}
                  disabled={fieldsDisabled || !form.enabled}
                  className="rounded border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-app-bg disabled:opacity-50"
                >
                  Adicionar
                </button>
              </div>
              {draftError !== null && (
                <p role="alert" className="mt-2 text-xs text-red-600">
                  {draftError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Override assimétrico */}
        <div
          className={`rounded-xl border border-border-subtle bg-surface p-4 ${form.enabled ? '' : 'opacity-50'}`}
        >
          <div className="flex items-center justify-between">
            <div className="pr-4">
              <h3 className="text-sm font-medium text-text-primary">
                Operador pode se auto-marcar de plantão
              </h3>
              <p className="mt-0.5 text-xs text-text-secondary">
                Se ligado, o próprio operador pode entrar de plantão (passa a receber urgências fora
                do expediente). Ele nunca se auto-habilita a receber trabalho comum fora da janela —
                o plantão só resgata os alertas urgentes.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.operatorCanSelfMarkOnCall}
              aria-label="Operador pode se auto-marcar de plantão"
              disabled={fieldsDisabled || !form.enabled}
              onClick={() => {
                setForm((f) =>
                  f === null
                    ? f
                    : { ...f, operatorCanSelfMarkOnCall: !f.operatorCanSelfMarkOnCall },
                );
              }}
              className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                form.operatorCanSelfMarkOnCall ? 'bg-accent' : 'bg-border-subtle'
              }`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                  form.operatorCanSelfMarkOnCall ? 'translate-x-[18px]' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Descartar/Salvar saíram — vivem na StickySaveBar do domínio (Admin Hub
          T9); sucesso/erro do save viram toast da barra, não mensagem local.
          O aviso de "sem janelas" continua vivo: descreve o estado ATUAL do
          form (honestidade), não o resultado de um save — não migra pro toast. */}
      {noWindows && (
        <p className="mt-6 text-xs text-amber-600">
          Sem janelas: o push de trabalho ficará sempre em silêncio (só plantão urgente fura).
        </p>
      )}
      {!canManage && (
        <p className="mt-6 rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          Você tem acesso de leitura. A edição destas configurações é restrita ao administrador do
          tenant.
        </p>
      )}
    </div>
  );
}
